import { Schema } from "effect";
import { schemaDecoder } from "./schema-compat.ts";

export const TestConcurrencyBindingPlan = /* @__PURE__ */ (() => Schema.Struct({
  workers: Schema.Record(Schema.String, Schema.mutableKey(Schema.Struct({
    workerName: Schema.String.pipe(Schema.mutableKey),
  }))).pipe(Schema.mutableKey),
  browsers: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)).pipe(Schema.mutableKey),
  containers: Schema.Record(Schema.String, Schema.mutableKey(Schema.Unknown)).pipe(Schema.mutableKey),
}))();
export type TestConcurrencyBindingPlan = typeof TestConcurrencyBindingPlan.Type;

export const MAX_TEST_CONCURRENCY = 4;
export const MAX_IN_FILE_TEST_CONCURRENCY = 5;

const PositiveConcurrency = /* @__PURE__ */ (() => Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)))();
export const TestConcurrency = /* @__PURE__ */ (() => Schema.UndefinedOr(PositiveConcurrency.check(
  Schema.isLessThanOrEqualTo(MAX_TEST_CONCURRENCY)
)))();
export type TestConcurrency = typeof TestConcurrency.Type;
export const isTestConcurrency = /* @__PURE__ */ (() => Schema.is(TestConcurrency))();
const concurrencyContract = /* @__PURE__ */ (() => schemaDecoder(PositiveConcurrency, () => new RangeError("concurrency must be a positive safe integer")))();

export function resolveTestConcurrency(
  requested: number,
  fileCount: number,
  plan: TestConcurrencyBindingPlan,
  workerName: string
): number {
  const crossWorker = Object.values(plan.workers).some(
    (binding: { workerName: string }) => binding.workerName !== workerName
  );
  const resourceHeavy =
    Object.keys(plan.browsers).length > 0 ||
    Object.keys(plan.containers).length > 0;
  return Math.min(
    requested,
    fileCount,
    crossWorker ? 1 : resourceHeavy ? 2 : MAX_TEST_CONCURRENCY
  );
}

export async function mapWithConcurrency<Input, Output>(
  inputs: readonly Input[],
  concurrency: number,
  mapper: (input: Input, index: number) => Output | PromiseLike<Output>
): Promise<Output[]> {
  concurrencyContract.parse(concurrency);

  const results = new Array<Output>(inputs.length);
  const failures = new Array<{ error: unknown } | undefined>(inputs.length);
  let nextIndex = 0;
  let stopped = false;

  const worker = async () => {
    while (!stopped && nextIndex < inputs.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        results[index] = await mapper(inputs[index]!, index);
      } catch (error) {
        failures[index] = { error };
        stopped = true;
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, inputs.length) }, worker)
  );

  const failure = failures.find((candidate) => candidate !== undefined);
  if (failure) throw failure.error;
  return results;
}
